import {
  UPDATE_FUNDING_REQUEST,
  UPDATE_LOAN_BALANCE,
  CODAT_PORTFOLIO_SYNC,
} from '@common/constants/webSockets'
import { applyServicingAuditAcknowledgement } from '@common/helpers/servicingAudit'
import {
  IPendingCashDominionData,
  IAllPendingCashDominionData,
  IBankTransactionRaw,
  IBankTransactionRawData,
} from '@common/interfaces/bankTransactions'
import {
  IExtraReserveData,
  IArIneligibilityData,
  IFundingRequest,
  IInventoryIneligibilityCustomRule,
  IInventoryIneligibilityData,
  IInventoryIneligibilityField,
  IMasterInvoice,
  IMasterInvoicesData,
  IMasterInvoiceBbcHistoryData,
  IMasterInvoicePaymentActivity,
  IOveradvance,
} from '@common/interfaces/bbc'
import {
  IAccountActivityGraphData,
  IAccountActivityData,
  IAccountActivityDateRange,
  IAmendmentsData,
  IClientBanner,
  IClientCashFlowData,
  IClientComplianceData,
  IClientComplianceSummaryMetrics,
  IClientInfo,
  IClientLoanLedgerData,
  IClientsAggregation,
  ICollectionsReconciliationData,
  ICollectionsReconciliationOptions,
  IComplianceEntityDetailRow,
  IComplianceStatusRow,
  IInventoryHealthData,
  IInventoryLocationOption,
  IClientBBCsListData,
  IRiskRatingData,
  IRiskRatingSummaryData,
  ICustomerCollectionsData,
  ISalesChannelDistributionData,
  IMonthlyExhibit,
  ICustomerInvoicingVsCollections,
  IFinancialsComparisonData,
  IDashboardMetricsData,
  ISalesChannelDistributionOptions,
  IRiskRatingSummaryHeaderData,
  ITermLoanOveradvanceItem,
  ITermLoansOveradvancesData,
} from '@common/interfaces/client'
import { ICheckAliasData } from '@common/interfaces/collection'
import {
  IServicingAuditIssueAcknowledgement,
  IServicingAuditResponse,
} from '@common/interfaces/servicingAudit'
import { StressCaseData } from '@common/interfaces/stressCase'
import { ITermLoan } from '@common/interfaces/termLoan'
import { IUser } from '@common/interfaces/user'
import { Action, combineReducers } from 'redux'
import { IInterestRateTier } from '../../api/interestRateTier'
import { loadingArrayReducer } from '../../helpers/helpers'
import { createLoadingDataReducer } from '../../helpers/redux'
import { IAction, IActionWithParams, IPaginationParams } from '../apiMiddleware'
import {
  REJECT_PREDICTED_CATEGORY,
  UNREJECT_PREDICTED_CATEGORY,
  UPDATE_TRANSACTIONS_REQUEST,
} from '../bankTransactions/actions'
import * as bbcActions from '../bbc/actions'
import { ILoadingData } from '../types'
import * as actions from './actions'

// After an edit (update / reject / unreject) the service returns the touched rows under `items`
// shaped like the list. Merge them into the loaded portfolio list in place, by id, instead of
// refetching — keeps the infinite-scroll position and costs O(affected rows), not O(rows loaded).
const mergePortfolioBankTransactionRows = (
  state: ILoadingData<IBankTransactionRawData>,
  action: Action & { data?: { items?: IBankTransactionRaw[] } },
): ILoadingData<IBankTransactionRawData> => {
  const items = action.data?.items
  const current = state.data
  if (!items?.length || !current?.data) {
    return state
  }
  const itemsById = new Map(items.map((item) => [item.id, item]))
  return {
    ...state,
    data: {
      ...current,
      data: current.data.map((row) => {
        const updated = itemsById.get(row.id)
        return updated ? { ...row, ...updated } : row
      }),
    },
  }
}

// Marking an audit issue as intended (or reopening it) patches the loaded audit in place; the
// table filters locally, so no refetch is needed.
const applyAuditAcknowledgement = (
  state: ILoadingData<IServicingAuditResponse>,
  action: Action & { data?: IServicingAuditIssueAcknowledgement },
): ILoadingData<IServicingAuditResponse> =>
  state.data && action.data?.issueKey
    ? { ...state, data: applyServicingAuditAcknowledgement(state.data, action.data) }
    : state

export default combineReducers({
  loadingArray: loadingArrayReducer(
    [actions.prefix],
    [
      actions.LIST_INVENTORY_ELIGIBLE_CATEGORIES_REQUEST,
      actions.LIST_MASTER_INVENTORY_REQUEST,
      actions.LIST_INVENTORY_ELIGIBLE_FIELDS_REQUEST,
      actions.UPDATE_INVENTORY_INELIGIBILITY_FIELDS_REQUEST,
      actions.LIST_AR_ELIGIBILITY_REQUEST,
      actions.LIST_AP_ELIGIBILITY_REQUEST,
      actions.UPDATE_AP_ELIGIBILITY_REQUEST,
      actions.LIST_COMPLIANCE_REQUEST,
      actions.LIST_COMPLIANCE_STATUS_LEGAL_REQUEST,
      actions.LIST_COMPLIANCE_STATUS_FINANCIALS_REQUEST,
      actions.LIST_INVENTORY_LOCATIONS_REQUEST,
      actions.LIST_CLIENT_FINANCIALS_REPORTS_REQUEST,
      actions.LIST_AMORTIZATION_SCHEDULE_REQUEST,
      actions.UPDATE_CLIENT_TERM_LOAN_REQUEST,
      actions.CREATE_TERM_LOAN_ACTIVITY_REQUEST,
      actions.DELETE_TERM_LOAN_ACTIVITY_REQUEST,
      actions.UPDATE_TERM_LOAN_ACTIVITY_REQUEST,
      actions.LIST_MASTER_INVOICES_REQUEST,
      actions.UPDATE_MASTER_INVOICE_REQUEST,
      actions.LIST_INTEREST_RATE_TIERS_REQUEST,
      actions.LIST_BBCS_REQUEST,
    ],
  ),
  clients: createLoadingDataReducer<{ data: IClientInfo[] }>([actions.LIST_REQUEST], [], true),

  clientInfo(state: IClientInfo | null = null, action: IAction): IClientInfo | null {
    switch (action.type) {
      case actions.SHOW_SUCCESS:
      case actions.UPDATE_SUCCESS:
      case actions.UPDATE_CLIENT_UNDERWRITING_SUCCESS:
        return action.data as IClientInfo
      case actions.CALCULATE_TERMINATE_SUCCESS:
      case actions.TERMINATE_SUCCESS: {
        const data = action.data as Partial<IClientInfo>
        return {
          ...state,
          ...data,
        }
      }
      case UPDATE_LOAN_BALANCE: {
        const data = action.data as {
          clientId: string
          loanBalance: number
          totalLoanBalance: number
          totalParticipantExposureAmount: number
          totalNFE: number
        }
        if (!state || state.id !== data.clientId) {
          return state
        }
        return {
          ...state,
          currentAmount: data.loanBalance,
          totalLoanBalance: data.totalLoanBalance,
          totalParticipantExposureAmount: data.totalParticipantExposureAmount,
          totalNFE: data.totalNFE,
        }
      }
      case actions.REORDER_OVERADVANCES_SUCCESS:
      case actions.CREATE_CLIENT_OVERADVANCE_SUCCESS: {
        const data = action.data as { overadvancePriorityOrder?: string[] }
        if (!state || !data?.overadvancePriorityOrder) return state
        return {
          ...state,
          overadvancePriorityOrder: data.overadvancePriorityOrder,
        }
      }
      case actions.HIDE:
        return null
      default:
        return state
    }
  },
  RMUsers: createLoadingDataReducer<{ data: IUser[] }>([actions.LIST_RM_USERS_REQUEST], [], true),

  managers: createLoadingDataReducer<{ data: IUser[] }>(
    [actions.LIST_MANAGERS_REQUEST],
    [
      actions.ADD_MANAGER_REQUEST,
      actions.REMOVE_MANAGER_REQUEST,
      actions.UPDATE_MANAGER_REQUEST,
      actions.NOTIFY_REQUEST,
    ],
    true,
  ),
  accountActivity: createLoadingDataReducer<IAccountActivityData>(
    [actions.LIST_ACCOUNT_ACTIVITY_SUCCESS],
    [],
    false,
    [actions.HIDE, actions.HIDE_ACCOUNT_ACTIVITY],
  ),
  accountActivityDateRange: createLoadingDataReducer<IAccountActivityDateRange>(
    [actions.GET_ACCOUNT_ACTIVITY_DATE_RANGE_REQUEST],
    [],
    false,
    [actions.HIDE],
  ),
  complianceStatusLegal: createLoadingDataReducer<IComplianceStatusRow[]>(
    [actions.LIST_COMPLIANCE_STATUS_LEGAL_SUCCESS],
    [],
    false,
    [actions.HIDE],
  ),
  complianceStatusFinancials: createLoadingDataReducer<IComplianceStatusRow[]>(
    [actions.LIST_COMPLIANCE_STATUS_FINANCIALS_SUCCESS],
    [],
    false,
    [actions.HIDE],
  ),
  complianceEntityDetails: createLoadingDataReducer<IComplianceEntityDetailRow[]>(
    [actions.LIST_COMPLIANCE_ENTITY_DETAILS_SUCCESS],
    [],
    false,
    [actions.HIDE],
  ),
  inventoryLocations: createLoadingDataReducer<{ data: IInventoryLocationOption[] }>(
    [actions.LIST_INVENTORY_LOCATIONS_SUCCESS],
    [],
    false,
    [actions.HIDE],
  ),
  fundingRequest(state: IFundingRequest = null, action: IAction): IFundingRequest {
    switch (action.type) {
      case actions.GET_REQUEST_FUNDING_SUCCESS:
      case actions.REQUEST_FUNDING_SUCCESS:
      case actions.MODIFY_FUNDING_SUCCESS:
      case actions.CANCEL_FUNDING_SUCCESS:
        return action.data as IFundingRequest
      case UPDATE_FUNDING_REQUEST:
        return {
          ...state,
          bbcSignatureFile: action.data as IFundingRequest['bbcSignatureFile'],
        }
      default:
        return state
    }
  },
  clientsAggregation: createLoadingDataReducer<IClientsAggregation>(
    [actions.LIST_CLIENTS_AGGREGATION_SUCCESS],
    [UPDATE_LOAN_BALANCE],
    true,
    [],
    true,
  ),
  dashboardMetrics: createLoadingDataReducer<IDashboardMetricsData>([
    actions.LIST_DASHBOARD_METRICS_SUCCESS,
  ]),
  overadvances(state: IOveradvance[] = [], action: IAction): IOveradvance[] {
    switch (action.type) {
      case actions.LIST_CLIENT_OVERADVANCE_SUCCESS: {
        const data = action.data as { data: IOveradvance[] }
        return data.data
      }
      default:
        return state
    }
  },
  complianceData: createLoadingDataReducer<IClientComplianceData>(
    [actions.LIST_COMPLIANCE_SUCCESS],
    [actions.UPDATE_SUCCESS],
  ),
  complianceSummaryMetrics: createLoadingDataReducer<IClientComplianceSummaryMetrics>([
    actions.LIST_COMPLIANCE_SUMMARY_METRICS_REQUEST,
  ]),
  complianceAudit: createLoadingDataReducer<IServicingAuditResponse>(
    [actions.LIST_COMPLIANCE_AUDIT_REQUEST],
    [actions.ACKNOWLEDGE_COMPLIANCE_AUDIT_ISSUE_REQUEST],
    true,
    undefined,
    false,
    applyAuditAcknowledgement,
  ),
  termLoans(state: ITermLoan[] = [], action: IAction): ITermLoan[] {
    switch (action.type) {
      case actions.LIST_CLIENT_TERM_LOAN_SUCCESS:
        return action.data as ITermLoan[]
      case actions.UPDATE_CLIENT_TERM_LOAN_SUCCESS: {
        const data = action.data as ITermLoan
        return state.map((termLoan) =>
          termLoan.id === data.id
            ? {
                ...termLoan,
                ...data,
              }
            : termLoan,
        )
      }
      case actions.CREATE_CLIENT_TERM_LOAN_SUCCESS: {
        const data = action.data as ITermLoan
        return [data, ...state]
      }

      case actions.DELETE_CLIENT_TERM_LOAN_SUCCESS: {
        const data = action.data as { id: string }
        return state.filter((termLoan) => termLoan.id !== data.id)
      }

      case actions.LIST_AMORTIZATION_SCHEDULE_SUCCESS: {
        const data = action.data as { termLoanId: string; data: ITermLoan['amortizationSchedule'] }
        const termLoanId = data?.termLoanId
        // Bail out on a response with nothing to attach rather than minting a
        // new array. Consumers key effects off this list's identity, so a
        // gratuitous new reference can retrigger the very fetch that produced it.
        if (!termLoanId || !state.some((termLoan) => termLoan.id === termLoanId)) {
          return state
        }
        return state.map((termLoan) =>
          termLoan.id === termLoanId
            ? {
                ...termLoan,
                amortizationSchedule: data.data,
              }
            : termLoan,
        )
      }
      default:
        return state
    }
  },
  interestRateTiers: createLoadingDataReducer<{ tiers: IInterestRateTier[] }>(
    [actions.LIST_INTEREST_RATE_TIERS_REQUEST],
    [],
    false,
    [actions.HIDE],
  ),
  inventoryEligibilityData: createLoadingDataReducer<IInventoryIneligibilityData>(
    [actions.LIST_MASTER_INVENTORY_REQUEST],
    [bbcActions.UPDATE_MASTER_INVENTORY_REQUEST],
  ),
  fields: createLoadingDataReducer<{ data: IInventoryIneligibilityField[] }>(
    [actions.LIST_INVENTORY_ELIGIBLE_FIELDS_REQUEST],
    [
      bbcActions.CREATE_INVENTORY_INELIGIBLE_FIELDS_REQUEST,
      bbcActions.DELETE_INVENTORY_INELIGIBLE_FIELDS_REQUEST,
      actions.UPDATE_INVENTORY_INELIGIBILITY_FIELDS_REQUEST,
    ],
  ),
  customRules: createLoadingDataReducer<IInventoryIneligibilityCustomRule[]>(
    [actions.LIST_INVENTORY_ELIGIBLE_CUSTOM_RULES_REQUEST],
    [actions.UPDATE_INVENTORY_INELIGIBILITY_FIELDS_REQUEST],
  ),
  customRulesLabels(state: string[] = [], action: IAction): string[] {
    switch (action.type) {
      case actions.LIST_INVENTORY_ELIGIBLE_CUSTOM_RULES_LABELS_SUCCESS:
        return action.data as string[]
      default:
        return state
    }
  },
  customRulesValues(
    state: { [key: string]: string[] } = {},
    action: IAction & { field: string },
  ): { [key: string]: string[] } {
    switch (action.type) {
      case actions.LIST_INVENTORY_ELIGIBLE_CUSTOM_RULES_VALUES_SUCCESS:
        return {
          ...state,
          [action.field]: action.data as string[],
        }
      default:
        return state
    }
  },
  amendmentsData(
    state: IAmendmentsData = null,
    action: IActionWithParams<IPaginationParams>,
  ): IAmendmentsData {
    switch (action.type) {
      case actions.LIST_AMENDMENTS_SUCCESS: {
        const data = action.data as IAmendmentsData
        if (!action?.params?.loadMore) {
          return data
        } else {
          return {
            ...state,
            amendments: [...state.amendments, ...data.amendments],
          }
        }
      }
      case actions.CREATE_AMENDMENT_SUCCESS: {
        const data = action.data as IAmendmentsData['amendments'][number]
        return {
          ...state,
          amendments: [data, ...state.amendments],
        }
      }
      case actions.UPDATE_AMENDMENT_SUCCESS: {
        const data = action.data as IAmendmentsData['amendments'][number]
        return {
          ...state,
          amendments: state.amendments.map((amendment) =>
            amendment.id === data.id ? data : amendment,
          ),
        }
      }
      case actions.DELETE_AMENDMENT_SUCCESS: {
        const data = action.data as { id: string }
        return {
          ...state,
          amendments: state.amendments.filter((amendment) => amendment.id !== data.id),
        }
      }
      case actions.LIST_OVERADVANCE_ACTIVITIES_SUCCESS: {
        const data = action.data as NonNullable<IOveradvance['overadvanceActivities']>
        return {
          ...state,
          amendments: state.amendments.map((term) => {
            if (term?.overadvance?.id === data?.[0]?.overadvanceId) {
              return {
                ...term,
                overadvance: {
                  ...(term.overadvance as IOveradvance),
                  overadvanceActivities: data,
                },
              } as typeof term
            }
            return term
          }),
        }
      }

      default:
        return state
    }
  },
  banners(state: IClientBanner[] = [], action: IAction): IClientBanner[] {
    switch (action.type) {
      case actions.GET_BANNER_SUCCESS:
        return action.data as IClientBanner[]
      case actions.CLOSE_BANNER_SUCCESS: {
        const data = action.data as { id: string }
        return state.filter(({ id }) => id !== data.id)
      }
      default:
        return state
    }
  },
  arEligibilityData: createLoadingDataReducer<IArIneligibilityData>(
    [actions.LIST_AR_ELIGIBILITY_REQUEST],
    [bbcActions.UPDATE_AR_INELIGIBILITY_REQUEST],
  ),
  apEligibilityData: createLoadingDataReducer<IExtraReserveData>(
    [actions.LIST_AP_ELIGIBILITY_REQUEST],
    [actions.UPDATE_AP_ELIGIBILITY_REQUEST],
  ),
  financialsReports: createLoadingDataReducer<{
    asOfDates: string[]
    startDate: string
    projectionsStartDate: string
  }>([actions.LIST_CLIENT_FINANCIALS_REPORTS_REQUEST], [], false, [
    actions.HIDE_CLIENT_FINANCIALS_REPORTS,
  ]),
  riskRatings: createLoadingDataReducer<IRiskRatingData>([actions.LIST_RISK_RATING_REQUEST]),
  clientBBCsList: createLoadingDataReducer<IClientBBCsListData>([actions.LIST_BBCS_REQUEST]),
  riskRatingDetails: createLoadingDataReducer<IRiskRatingSummaryData>(
    [actions.LIST_RISK_RATING_DETAILS_REQUEST],
    [UPDATE_LOAN_BALANCE],
    true,
    [],
    true,
    (state, action: IAction) => {
      const data = action.data as {
        clientId: string
        recordDate: string
        loanBalance: number
      }
      return {
        ...state,
        data: {
          ...state.data,
          data: state?.data?.data?.map((item) =>
            item.clientInfo?.id === data.clientId
              ? {
                  ...item,
                  clientInfo: {
                    ...item.clientInfo,
                    loanBalance:
                      data.recordDate ===
                      (
                        item.clientInfo.loanBalance as {
                          recordDate?: string
                          loanBalance: number
                        }
                      ).recordDate
                        ? {
                            ...item.clientInfo.loanBalance,
                            loanBalance: data.loanBalance,
                          }
                        : item.clientInfo.loanBalance,
                  },
                }
              : item,
          ),
        },
      }
    },
  ),
  inventoryHealth: createLoadingDataReducer<IInventoryHealthData>([
    actions.LIST_INVENTORY_HEALTH_REQUEST,
  ]),
  cashFlowInfo: createLoadingDataReducer<IClientCashFlowData>([
    actions.SHOW_CASH_FLOW_SUMMARY_INFO_REQUEST,
  ]),
  customers: createLoadingDataReducer<{
    data: string[]
  }>([actions.LIST_CUSTOMERS_REQUEST]),
  refreshCount: (state = 0, action: IAction) => {
    switch (action.type) {
      case actions.REFRESH_COUNT:
      case CODAT_PORTFOLIO_SYNC:
        return state + 1
    }

    return state
  },
  accountActivityGraph: createLoadingDataReducer<IAccountActivityGraphData>(
    [actions.ACCOUNT_ACTIVITY_GRAPH_REQUEST],
    [],
    false,
    [actions.HIDE],
  ),
  loanLedger: createLoadingDataReducer<IClientLoanLedgerData>([actions.LIST_LOAN_LEDGER_REQUEST]),
  stressCase: createLoadingDataReducer<StressCaseData>(
    [actions.LIST_STRESS_CASE_ASSUMPTIONS_REQUEST],
    [actions.UPDATE_STRESS_CASE_ASSUMPTION_REQUEST],
  ),
  collectionsReconciliation: createLoadingDataReducer<ICollectionsReconciliationData>(
    [actions.LIST_COLLECTIONS_RECONCILIATION_REQUEST],
    [],
    false,
    [actions.HIDE_COLLECTIONS_RECONCILIATION],
  ),
  collectionsReconciliationOptions: createLoadingDataReducer<ICollectionsReconciliationOptions>([
    actions.LIST_COLLECTIONS_RECONCILIATION_OPTIONS_REQUEST,
  ]),
  customerCollections: createLoadingDataReducer<ICustomerCollectionsData>([
    actions.LIST_CUSTOMER_COLLECTIONS_REQUEST,
  ]),
  salesChannelDistribution: createLoadingDataReducer<ISalesChannelDistributionData>(
    [actions.LIST_SALES_CHANNEL_DISTRIBUTION_REQUEST],
    [],
    false,
    [actions.HIDE_SALES_CHANNEL_DISTRIBUTION],
  ),
  salesChannelDistributionOptions: createLoadingDataReducer<ISalesChannelDistributionOptions>([
    actions.LIST_SALES_CHANNEL_DISTRIBUTION_OPTIONS_REQUEST,
  ]),
  arLedgerActivity: createLoadingDataReducer<IMonthlyExhibit>([
    actions.LIST_AR_LEDGER_ACTIVITY_REQUEST,
  ]),
  customerInvoicingVsCollections: createLoadingDataReducer<ICustomerInvoicingVsCollections>([
    actions.LIST_CUSTOMER_INVOICING_VS_COLLECTIONS_REQUEST,
  ]),
  pendingCashDominion: createLoadingDataReducer<IPendingCashDominionData>([
    actions.LIST_PENDING_CASH_DOMINION_REQUEST,
  ]),
  allPendingCashDominion: createLoadingDataReducer<IAllPendingCashDominionData>([
    actions.LIST_ALL_PENDING_CASH_DOMINION_REQUEST,
  ]),
  financialsComparison: createLoadingDataReducer<IFinancialsComparisonData>([
    actions.LIST_FINANCIALS_COMPARISON_REQUEST,
  ]),
  checkAliases: createLoadingDataReducer<ICheckAliasData>([
    actions.LIST_CHECK_ALIASES_REQUEST,
    actions.UPDATE_CHECK_ALIASES_REQUEST,
  ]),
  riskRatingDetailsChart: createLoadingDataReducer<IRiskRatingSummaryData>([
    actions.LIST_RISK_RATING_DETAILS_CHART_REQUEST,
  ]),
  riskRatingDetailsNoFilters: createLoadingDataReducer<IRiskRatingSummaryHeaderData>([
    actions.LIST_RISK_RATING_DETAILS_NO_FILTERS_REQUEST,
  ]),
  termLoansOveradvances: createLoadingDataReducer<ITermLoansOveradvancesData>([
    actions.LIST_TERM_LOANS_OVERADVANCES_REQUEST,
  ]),
  clientOveradvances: createLoadingDataReducer<ITermLoanOveradvanceItem[]>([
    actions.LIST_CLIENT_OVERADVANCES_REQUEST,
  ]),
  portfolioBankTransactionsRaw: createLoadingDataReducer<IBankTransactionRawData>(
    [actions.LIST_PORTFOLIO_BANK_TRANSACTIONS_RAW_REQUEST],
    // Edit actions carry the touched rows on success; the customLiveEdit below merges them in place.
    [
      actions.HIDE_PORTFOLIO_BANK_TRANSACTIONS_DATA,
      UPDATE_TRANSACTIONS_REQUEST,
      REJECT_PREDICTED_CATEGORY[0],
      UNREJECT_PREDICTED_CATEGORY[0],
    ],
    true,
    undefined,
    false,
    mergePortfolioBankTransactionRows,
  ),
  masterInvoiceData: createLoadingDataReducer<IMasterInvoice>([actions.GET_MASTER_INVOICE_REQUEST]),
  masterInvoicesData: createLoadingDataReducer<IMasterInvoicesData>(
    [actions.LIST_MASTER_INVOICES_REQUEST],
    [actions.UPDATE_MASTER_INVOICE_REQUEST],
  ),
  masterInvoiceBbcHistory: createLoadingDataReducer<IMasterInvoiceBbcHistoryData>([
    actions.LIST_MASTER_INVOICE_BBC_HISTORY_REQUEST,
  ]),
  masterInvoicePaymentActivity: createLoadingDataReducer<IMasterInvoicePaymentActivity[]>([
    actions.GET_MASTER_INVOICE_PAYMENT_ACTIVITY_REQUEST,
  ]),
})
